import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always run /auth/me on mount:
    //   - If a valid session cookie exists (returning user), we hydrate immediately.
    //   - If not, it 401s → user stays null → SignInOverlay shows.
    // AuthCallback still handles the OAuth session_id exchange separately and calls
    // setUser + checkAuth to update this context.
    checkAuth();
  }, [checkAuth]);

  // Re-fetch balance whenever a generation fails and refunds credits (or
  // whenever any component broadcasts a credit change) so the navbar chip
  // reflects the fresh balance without a page reload.
  useEffect(() => {
    const onCreditsUpdated = () => { checkAuth(); };
    window.addEventListener("credits:updated", onCreditsUpdated);
    return () => window.removeEventListener("credits:updated", onCreditsUpdated);
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
